// Season calendar for holiday themes. Pure functions, local time.

export const EID_AL_FITR = [
  "2027-03-09", "2028-02-26", "2029-02-14", "2030-02-04", "2031-01-24",
  "2032-01-14", "2033-01-03", "2033-12-23", "2034-12-12", "2035-12-01",
];

// Seasonal keys currently shipped. Later releases add more.
export const RELEASED = ["christmas"];

const LEAD_DAYS = 10;

// Meeus/Jones/Butcher Gregorian algorithm.
export function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

export const SEASONS = {
  christmas: { religious: true, holiday: (y) => [new Date(y, 11, 25)], graceDays: 0 },
  carnival: {
    religious: false,
    holiday: (y) => {
      const e = easterSunday(y);
      return [new Date(y, e.getMonth(), e.getDate() - 47)];
    },
    graceDays: 0,
  },
  easter: { religious: true, holiday: (y) => [easterSunday(y)], graceDays: 0 },
  valentine: { religious: false, holiday: (y) => [new Date(y, 1, 14)], graceDays: 0 },
  eid: {
    religious: true,
    holiday: (y) =>
      EID_AL_FITR.filter((s) => s.startsWith(`${y}-`)).map((s) => {
        const [yy, mm, dd] = s.split("-").map(Number);
        return new Date(yy, mm - 1, dd);
      }),
    graceDays: 1,
  },
};

export function isSeasonalTheme(key, released = RELEASED) {
  return released.includes(key) && key in SEASONS;
}

function windowOf(key, holiday) {
  const s = SEASONS[key];
  const y = holiday.getFullYear();
  const m = holiday.getMonth();
  const day = holiday.getDate();
  return {
    start: new Date(y, m, day - LEAD_DAYS, 0, 0, 0, 0),
    end: new Date(y, m, day + s.graceDays, 23, 59, 59, 999),
    holiday,
  };
}

function windowsAround(key, year) {
  const out = [];
  for (let y = year - 1; y <= year + 1; y++) {
    for (const h of SEASONS[key].holiday(y)) out.push(windowOf(key, h));
  }
  return out.sort((a, b) => a.holiday - b.holiday);
}

// Window containing `now`, regardless of release status.
export function windowFor(key, now) {
  if (!(key in SEASONS)) return null;
  return windowsAround(key, now.getFullYear()).find((w) => now >= w.start && now <= w.end) || null;
}

export function currentWindow(key, now, released = RELEASED) {
  return isSeasonalTheme(key, released) ? windowFor(key, now) : null;
}

// Current window if in season, else the upcoming one.
export function nextWindow(key, now, released = RELEASED) {
  if (!isSeasonalTheme(key, released)) return null;
  const w = windowsAround(key, now.getFullYear()).find((x) => x.end >= now);
  return w ? { start: w.start, end: w.end, holiday: w.holiday } : null;
}

export function isThemeInSeason(key, now, released = RELEASED) {
  if (!isSeasonalTheme(key, released)) return true;
  return currentWindow(key, now, released) !== null;
}

export function activeSeason(now, released = RELEASED) {
  const rank = (k) => (k === "valentine" ? 0 : k === "carnival" ? 1 : 2);
  const live = released
    .filter((k) => isSeasonalTheme(k, released))
    .map((k) => ({ k, w: windowFor(k, now) }))
    .filter((x) => x.w);
  live.sort(
    (a, b) =>
      Number(SEASONS[a.k].religious) - Number(SEASONS[b.k].religious) ||
      rank(a.k) - rank(b.k) ||
      a.w.holiday - b.w.holiday
  );
  return live.length ? live[0].k : null;
}
